import { SAVED_GAME_RETIRED_MESSAGE, type BotDifficulty } from "@bing/shared";
import { Client } from "colyseus.js";
import type { GameResult } from "./connection";
import { noteSavedGameRetired, takeResumeSolo } from "./fresh-solo";
import { isSavedGameRetired } from "./join-error";
import { manageRoom, safeRoomLeave, type ConnectionRegistry } from "./lifecycle";
import { watchJoin } from "./join-watch";

/** Generic realtime connection to a solo game room, parameterised by room name +
 * move/view types. Every solo game (Pasur, Bluff, Hokm, Shelem, …) reuses this.
 * The room's `state` message carries the redacted view AND the human's legal
 * moves, so tables render controls from an authoritative list. */
export interface GameHandlers<M, V> {
  onState: (view: V, legal: readonly M[]) => void;
  onGameOver: (result: GameResult) => void;
  onError: (message: string) => void;
}

export interface GameRoomHandle<M> {
  send: (move: M) => void;
  /** «پایان بازی» — explicitly END the game server-side (drops its ActiveGame so it
   *  leaves the resume list). Resolves ONLY on the server's `ended` ack — i.e. after
   *  the row is provably deleted — and rejects on the server's `endFailed`, a dead
   *  connection, or {@link END_ACK_TIMEOUT_MS} of silence (the caller must then KEEP
   *  the game listed and surface the error). A plain {@link leave} KEEPS the game
   *  resumable, so ending it is this separate, deliberate round trip. */
  end: () => Promise<void>;
  leave: () => void;
  /** Tell the server the client finished LOADING (the loading bar filled) so it may start
   *  the bots. On a FRESH solo game the server holds the opening ceremony until this
   *  arrives; a resume/reconnect ignores it (already mid-play). */
  ready: () => void;
}

export type GameConnector<M, V> = (
  handlers: GameHandlers<M, V>,
  /** Aborted when the player leaves before the join has landed (the store's `disconnect` — the
   *  loading screen's exit). A join that lands after it is never managed: see the connector. */
  signal?: AbortSignal,
) => Promise<GameRoomHandle<M>>;

/** How long «پایان بازی» waits for the server's `ended` ack before failing. */
export const END_ACK_TIMEOUT_MS = 5000;

/** The subset of a colyseus.js `Room` the end-ack round trip needs — structural,
 *  so tests can drive it with a plain object (no WebSocket). */
export interface EndAckRoom {
  send: (type: string, message?: unknown) => void;
  onMessage: (type: string, callback: (message: unknown) => void) => unknown;
}

/**
 * Build a handle's `end()` for `room`: sends the `end` message and settles on the
 * server's answer. `ended` only arrives AFTER the server deleted the ActiveGame
 * row, so a resolved promise means the resume list really no longer has this game;
 * `endFailed`, a send failure, or silence past {@link END_ACK_TIMEOUT_MS} reject.
 * Listeners are registered once (colyseus.js `onMessage` is per-type); calling
 * `end()` again supersedes (rejects) a still-pending earlier attempt.
 */
export function makeEndRequester(room: EndAckRoom): () => Promise<void> {
  let pending: ((error?: Error) => void) | null = null;
  const settle = (error?: Error): void => {
    const p = pending;
    pending = null;
    p?.(error);
  };
  room.onMessage("ended", () => settle());
  room.onMessage("endFailed", () => settle(new Error("end failed")));
  return () =>
    new Promise<void>((resolve, reject) => {
      settle(new Error("superseded by a newer end request"));
      const timer = setTimeout(() => settle(new Error("end ack timeout")), END_ACK_TIMEOUT_MS);
      pending = (error) => {
        clearTimeout(timer);
        if (error) reject(error);
        else resolve();
      };
      try {
        room.send("end");
      } catch (error) {
        settle(error instanceof Error ? error : new Error(String(error)));
      }
    });
}

interface StateMessage<M, V> {
  view: V;
  legal: M[];
}

/**
 * The exact options a solo join sends — extracted as a PURE function so the WIRE is testable
 * without a socket. A setting that flips on screen and never reaches `joinOrCreate` is a setting
 * in name only, and that failure has shipped here before (the «طول مسابقه» picker rendered its
 * default and only WROTE on change, so a host who accepted it sent nothing).
 */
export function soloJoinOptions(
  opts: {
    getInitData: () => string | null;
    difficulty?: BotDifficulty;
    players?: number;
    matchLength?: number;
    modeKot?: boolean;
    hokmModes?: boolean;
    shelemTarget?: number;
    side?: number;
  },
  resume: string | null,
): Record<string, unknown> {
  return {
    initData: opts.getInitData() ?? "",
    ...(resume ? { resume } : {}),
    ...(opts.difficulty ? { difficulty: opts.difficulty } : {}),
    ...(opts.players ? { players: opts.players } : {}),
    ...(opts.matchLength ? { matchLength: opts.matchLength } : {}),
    // Sent ONLY when true: the server reads it as a strict boolean, so an absent key IS the
    // conventional OFF and a `false` on the wire would carry no extra meaning.
    ...(opts.modeKot === true ? { modeKot: true } : {}),
    // Same rule, same reason: the default is OFF (the standard game), and the server reads a
    // strict `true`, so an absent key IS the default.
    ...(opts.hokmModes === true ? { hokmModes: true } : {}),
    // شلم's target is SENT AS PICKED, the default ۶۶۰ included: the start screen renders a choice,
    // so accepting what it shows is a choice too (the «setting in name only» lesson above).
    ...(opts.shelemTarget === undefined ? {} : { shelemTarget: opts.shelemTarget }),
    // Sent even when 0 — seat 0 (White) is a real, chosen answer, not "unset".
    ...(opts.side === undefined ? {} : { side: opts.side }),
  };
}

export function createColyseusGameConnector<M, V>(opts: {
  wsUrl: string;
  room: string;
  getInitData: () => string | null;
  /** Chosen AI opponent strength for this solo game — sent so the server picks the
   *  matching bot (validated server-side; omitted → the server's default). */
  difficulty?: BotDifficulty;
  /** Seats at the table for games whose player count the user chooses (منچ: 2/3/4).
   *  Validated + clamped server-side; omitted → the room's registered default. */
  players?: number;
  /** حکم — rounds needed to win (3/5/7) — and دوز — how many دست the match RUNS (3/5). The two
   *  reach DIFFERENT engine rules (`targetPoints` vs `handsToPlay`, because «first to N» and
   *  «exactly N» are different games); `matchLengthRule` on the server decides which from the
   *  trusted room game. The SERVER validates and clamps it; a resume ignores it entirely, since
   *  the persisted state already carries the length the match started with. */
  matchLength?: number;
  /** حکم — the player's کوت-under-a-suit-less-call choice. Sent only when true; the server
   *  reads it only for حکم (`variableModeKot`) and only as a strict boolean. */
  modeKot?: boolean;
  /** حکم — may the Hakem call سرس / نرس / آس نرس in this game? Sent only when true; the server
   *  reads it only for حکم (`variableHokmModes`), only as a strict boolean, and ignores it on a
   *  resume — the persisted state carries the vocabulary the match was dealt under. */
  hokmModes?: boolean;
  /** شلم — the match target the player picked on the start screen (۶۶۰ / ۱۲۰۰, card-67). The
   *  server reads it only for شلم (`variableShelemTarget`), validates it as a private host's pick,
   *  and ignores it on a resume — the persisted state carries the target the match was dealt under. */
  shelemTarget?: number;
  /** ITEM 7 — the seat the human takes, for games that offer the choice (chess: 0 = White,
   *  1 = Black). Ignored by every room that did not opt in. */
  side?: number;
  /** Lifecycle registry (injected in tests; defaults to the app singleton). */
  registry?: ConnectionRegistry;
}): GameConnector<M, V> {
  return async (handlers, signal) => {
    const client = new Client(opts.wsUrl);
    /**
     * The saved-game row the CURRENT game occupies, as reported by the room's `saved`
     * message. A solo reconnect is a plain `joinOrCreate` into a FRESH room, and a saved
     * game is only ever addressed by id — so without naming this row again the reconnect
     * was dealt a brand-new game (and consumed another concurrent-games slot). Null until
     * the first `saved` arrives / for an unpersisted game.
     */
    let savedGameId: string | null = null;
    const joinOptions = (): Record<string, unknown> =>
      // ITEM 1 — consume the one-shot "resume THIS save" id here, so EVERY offline game is
      // covered by one place. Consumed, not merely read: the FIRST join carries it; a later
      // reconnect falls back to the row the server told us this game lives in, so it comes
      // back to the game in progress rather than starting another one.
      soloJoinOptions(opts, takeResumeSolo() ?? savedGameId);
    // «پایان بازی» rides the CURRENT room — re-bound on every reconnect.
    let end: () => Promise<void> = () => Promise.reject(new Error("not connected"));

    const initial = await watchJoin(
      { conn: `solo:${opts.room}`, kind: "solo" },
      client.joinOrCreate(opts.room, joinOptions()),
    ).catch((error: unknown) => {
      // The «ادامه بازی» join of a save the server RETIRED: the rejection goes on untouched (the
      // store shows the notice), and «ادامه بازی» is re-read at once (`noteSavedGameRetired`).
      if (isSavedGameRetired(error)) noteSavedGameRetired();
      throw error;
    });
    /**
     * THE PLAYER LEFT WHILE THE JOIN WAS IN FLIGHT (the loading screen's exit). Nobody holds this
     * room, so it is never handed to the lifecycle — a registered orphan is reconnected on every
     * drop and keeps a second room open on the same save — and it is closed CONSENTED: a solo room
     * reads that as «خروج», returns at once and KEEPS the save (`SoloRoom.onLeave`). Not the
     * online store's `drop`: a non-consented close only holds a 60 s reservation nobody will claim
     * (the web's solo reconnect is a FRESH `joinOrCreate`, never `client.reconnect`).
     */
    if (signal?.aborted) {
      void safeRoomLeave(initial);
      throw new Error(`solo:${opts.room}: left before the join landed`);
    }

    const managed = manageRoom({
      name: `solo:${opts.room}`,
      registry: opts.registry,
      initial,
      // SOLO reconnect is a PLAIN REJOIN: the server persists the in-progress
      // game (`ActiveGame`) at every turn boundary, so a fresh joinOrCreate
      // rehydrates the mid-game position and re-emits full state on join.
      reacquire: () =>
        client.joinOrCreate(opts.room, joinOptions()).catch((error: unknown) => {
          if (isSavedGameRetired(error)) {
            // THE SERVER RETIRED THIS SAVE ON A RE-JOIN (a game-server restart met a save made under
            // rules it no longer plays). It ENDS the loop here: the row is deleted, so a second
            // attempt would name nothing and be dealt a NEW game into the reconnect — or, behind a
            // gate, spend the budget into «اتصال برقرار نشد». Releasing unregisters, so the
            // lifecycle stops at its next checkpoint and takes its overlay down; the store then
            // shows the same notice as the «ادامه بازی» door, and «ادامه بازی» is re-read at once.
            managed.release();
            handlers.onError(SAVED_GAME_RETIRED_MESSAGE);
            noteSavedGameRetired();
          }
          throw error;
        }),
      wire: (room) => {
        // Which saved game this room is playing — remembered so a reconnect can name it.
        room.onMessage("saved", (msg: { id?: unknown }) => {
          if (typeof msg?.id === "string" && msg.id) savedGameId = msg.id;
        });
        room.onMessage("state", (msg: StateMessage<M, V>) => handlers.onState(msg.view, msg.legal));
        room.onMessage("gameover", (result: GameResult) => {
          managed.release(); // terminal — a finished game must never auto-rejoin
          handlers.onGameOver(result);
        });
        // A bare transport error (dead socket → no code/reason) is owned by the
        // lifecycle (onLeave → reconnect); surfacing a raw "Error undefined" would
        // race the Persian reconnect overlay. Only route a real server message.
        room.onError((code: number, message?: string) => {
          if (message) handlers.onError(message);
          else console.error(`[solo:${opts.room}] socket error ${code} — reconnecting`);
        });
        end = makeEndRequester(room);
      },
    });

    return {
      send: (move) => managed.send("move", move),
      end: () => end(),
      leave: () => managed.leave(),
      ready: () => managed.send("ready"),
    };
  };
}
